import React, { useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { myRiskQueries } from '../lib/queries';

export const MY_ORGANIZATION_PATH = '/my-organization';

const grade = (s: number) => (s >= 90 ? 'A' : s >= 80 ? 'B' : s >= 70 ? 'C' : s >= 60 ? 'D' : 'F');

/** Header entry to My Organization: the tenant's own company, assessed like a vendor. */
export default function OrganizationButton({ isDark, enabled }: { isDark: boolean; enabled: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useQuery({ ...myRiskQueries.status(), enabled });
  const profile = data?.profile ?? null;
  const score = profile?.score ?? null;
  const scanning = !!data?.scan_in_progress;
  const active = location.pathname === MY_ORGANIZATION_PATH;

  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [profile?.domain]);

  const c = {
    ink: isDark ? '#e2e8f0' : '#0F172A',
    muted: isDark ? '#7c8aa0' : '#64748B',
    line: isDark ? '#262626' : '#E2E8F0',
    hover: isDark ? '#121212' : '#F8FAFC',
    accent: isDark ? '#3B82F6' : '#2563EB',
    accentBg: isDark ? '#0f1e38' : '#EFF6FF',
    tile: isDark ? '#1a1a1a' : '#F1F5F9',
  };
  const tone = score === null ? null
    : score >= 80 ? { fg: isDark ? '#4ADE80' : '#15803D', bg: isDark ? '#0b2214' : '#F0FDF4' }
    : score >= 60 ? { fg: isDark ? '#FACC15' : '#B45309', bg: isDark ? '#241f08' : '#FFFBEB' }
    : { fg: isDark ? '#F87171' : '#B91C1C', bg: isDark ? '#2a0f0f' : '#FEF2F2' };

  const name = profile?.name || data?.company_name || 'Your company';
  const initials = name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const tooltip = !data ? 'My Organization'
    : !profile ? "Set up your company's own security profile"
    : `${profile.name} (${profile.domain})${score !== null ? ` · security score ${score}${profile.risk_level ? `, ${profile.risk_level} risk` : ''}` : ''}`;

  return (
    <Tooltip title={tooltip}>
      <Box
        component="button"
        onClick={() => navigate(MY_ORGANIZATION_PATH)}
        aria-current={active ? 'page' : undefined}
        aria-label="My Organization"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1.25, mr: 1.5, pl: 0.75, pr: 1, height: 44,
          borderRadius: '12px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          border: `1px solid ${active ? c.accent : c.line}`,
          bgcolor: active ? c.accentBg : 'transparent',
          transition: 'background-color .15s, border-color .15s',
          '&:hover': { bgcolor: active ? c.accentBg : c.hover, borderColor: active ? c.accent : (isDark ? '#333' : '#CBD5E1') },
          '&:focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: 2 },
        }}
      >
        {/* logo */}
        <Box sx={{
          width: 30, height: 30, borderRadius: '8px', flexShrink: 0, overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: profile && !logoFailed ? '#ffffff' : (profile ? c.tile : c.accentBg),
          border: `1px solid ${c.line}`,
          color: profile ? c.ink : c.accent, fontSize: 11, fontWeight: 800,
        }}>
          {profile && !logoFailed ? (
            <img src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(profile.domain)}&sz=64`} alt=""
              width={20} height={20} style={{ objectFit: 'contain' }} onError={() => setLogoFailed(true)} />
          ) : profile ? initials : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 10h.01M15 10h.01" />
            </svg>
          )}
        </Box>

        {/* label + company */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', minWidth: 0, lineHeight: 1.15 }}>
          <Box component="span" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: active ? c.accent : c.muted }}>
            My Organization
          </Box>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.ink, maxWidth: 170, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: '2px' }}>
            {data && !profile ? 'Set up your profile' : name}
          </Box>
        </Box>

        {/* status */}
        {data && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: { xs: 0, md: 1.25 }, ml: { xs: 0, md: 0.25 }, borderLeft: { xs: 'none', md: `1px solid ${c.line}` }, height: 26 }}>
            {!profile ? (
              <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: c.accent, whiteSpace: 'nowrap' }}>Set up →</Box>
            ) : scanning && score === null ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 600, color: c.muted, whiteSpace: 'nowrap' }}>
                <Box component="span" sx={{
                  width: 7, height: 7, borderRadius: '50%', bgcolor: c.accent,
                  animation: 'orgPulse 1.1s ease-in-out infinite alternate',
                  '@keyframes orgPulse': { from: { opacity: 0.3 }, to: { opacity: 1 } },
                }} />
                Scanning
              </Box>
            ) : score !== null && tone ? (
              <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'baseline', gap: 0.5, px: 1, py: '3px', borderRadius: '8px',
                bgcolor: tone.bg, color: tone.fg, whiteSpace: 'nowrap',
              }}>
                <Box component="span" sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1 }}>{score}</Box>
                <Box component="span" sx={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>{grade(score)}</Box>
              </Box>
            ) : (
              <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: c.muted, whiteSpace: 'nowrap' }}>Not scanned</Box>
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
